// Block / Unblock / Delete for one product user.
//
// These are the whole of what the old User Administration page did. That page
// was a second copy of the Explorer's people list, so it was deleted and its two
// actions moved here — rendered on the Explorer's people list AND on a person's
// detail page, from ONE implementation, so the confirm copy and the cache
// invalidation cannot drift between the two places.
//
// Gating: `user_administration` is now a CAPABILITY grant with no page of its
// own. Holders see these buttons; everyone else sees nothing at all (not a
// disabled button — an action you may never take is noise). The backend is still
// the real gate: both routes are `require_area("user_administration")` and 403
// a caller without it, so hiding the buttons is UX, never the security boundary.
import { Badge, Button, ConfirmDialog, useToast } from "@recruiter/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { deleteUser, setUserBlock } from "../lib/api";
import { useCanWrite } from "../lib/use-access";
import { adminKeys } from "../lib/queries";
import { errorMessage } from "./states";

/** The minimum a caller must know about the user to act on them. Deliberately
 *  narrow so both the list row and the detail header can satisfy it. */
export interface UserAdminTarget {
  id: string;
  email: string;
  blocked: boolean;
}

/** Does the signed-in employee hold `user_administration` at FULL access?
 *
 *  Every button behind this blocks or deletes an account, and the routes take the
 *  `full` level, so a read-only holder would be drawn a control the server is
 *  about to refuse. `user_administration` has no read half at all — see
 *  `web/deps/__init__.py`. */
export function useCanAdministerUsers(): boolean {
  return useCanWrite("user_administration");
}

export function UserAdminActions({
  user,
  size = "sm",
}: {
  user: UserAdminTarget;
  /** `sm` on table rows, `md` in a detail header. */
  size?: "sm" | "md";
}) {
  const canAdminister = useCanAdministerUsers();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [pendingDelete, setPendingDelete] = useState(false);
  const [pendingBlock, setPendingBlock] = useState(false);

  /** Both the list and the detail read this user, so both caches are refreshed
   *  after a write. Explicit invalidation, never a manual cache patch. */
  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: adminKeys.explorerUsers });
    void queryClient.invalidateQueries({ queryKey: adminKeys.explorerUser(user.id) });
  }

  const blockMutation = useMutation({
    mutationFn: (blocked: boolean) => setUserBlock(user.id, blocked),
    onSuccess: (_data, blocked) => {
      setPendingBlock(false);
      invalidate();
      toast.add({
        title: blocked ? "User blocked" : "User unblocked",
        description: user.email,
        data: { tone: blocked ? "warning" : "success" },
      });
    },
    onError: (error) => {
      setPendingBlock(false);
      toast.add({
        title: "Couldn't update block state",
        description: errorMessage(error),
        data: { tone: "danger" },
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteUser(user.id),
    onSuccess: () => {
      setPendingDelete(false);
      invalidate();
      toast.add({ title: "User deleted", description: user.email, data: { tone: "success" } });
    },
    onError: (error) => {
      setPendingDelete(false);
      toast.add({
        title: "Delete failed",
        description: errorMessage(error),
        data: { tone: "danger" },
      });
    },
  });

  if (!canAdminister) return null;

  const busy = blockMutation.isPending || deleteMutation.isPending;

  return (
    <>
      <div className="rc-row-actions">
        <Button
          variant="outline"
          tone={user.blocked ? "accent" : "neutral"}
          size={size}
          disabled={busy}
          // Unblocking is reversible and restores normal service, so it goes
          // through immediately. Blocking stops the assistant answering a real
          // person, so it asks first. Same asymmetry the old page had.
          onClick={() => (user.blocked ? blockMutation.mutate(false) : setPendingBlock(true))}
        >
          {user.blocked ? "Unblock" : "Block"}
        </Button>
        <Button
          variant="outline"
          tone="danger"
          size={size}
          disabled={busy}
          onClick={() => setPendingDelete(true)}
        >
          Delete
        </Button>
      </div>

      <ConfirmDialog
        open={pendingBlock}
        title="Block this user?"
        message={`Block ${user.email}. They can still open the app, but the assistant will stop responding to their messages until you unblock them.`}
        confirmLabel="Block user"
        tone="danger"
        busy={blockMutation.isPending}
        onConfirm={() => blockMutation.mutate(true)}
        onCancel={() => setPendingBlock(false)}
      />

      <ConfirmDialog
        open={pendingDelete}
        title="Delete this user?"
        message={`Permanently delete ${user.email} and everything tied to their account: projects, conversations, files, and exports. This cannot be undone.`}
        confirmLabel="Delete permanently"
        tone="danger"
        busy={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setPendingDelete(false)}
      />
    </>
  );
}

/** The "Blocked" marker, so a blocked row reads as blocked even for someone
 *  without the grant (who sees no buttons). */
export function BlockedBadge({ blocked }: { blocked: boolean }) {
  if (!blocked) return null;
  return (
    <Badge tone="danger" size="sm">
      Blocked
    </Badge>
  );
}
